/**
 * API магазинов отдают UTC, а магазин живёт по киевскому времени. Границы
 * «суток» и «месяца» считаем в этой зоне, иначе отчёты съезжают.
 */
export const SHOP_TIME_ZONE = 'Europe/Kyiv';
/** `2026-10` в указанной зоне */
export function monthKey(date = new Date(), timeZone = SHOP_TIME_ZONE) {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
    }).formatToParts(date);
    const year = parts.find((p) => p.type === 'year')?.value ?? '0000';
    const month = parts.find((p) => p.type === 'month')?.value ?? '01';
    return `${year}-${month}`;
}
/** Смещение зоны от UTC в минутах на указанный момент (учитывает переход на лето). */
function zoneOffsetMinutes(at, timeZone) {
    const asUtc = new Date(at.toLocaleString('en-US', { timeZone: 'UTC' }));
    const asZone = new Date(at.toLocaleString('en-US', { timeZone }));
    return Math.round((asZone.getTime() - asUtc.getTime()) / 60000);
}
/** Начало суток в зоне магазина, в UTC. */
export function startOfDay(date = new Date(), timeZone = SHOP_TIME_ZONE) {
    const offset = zoneOffsetMinutes(date, timeZone);
    const local = new Date(date.getTime() + offset * 60000);
    local.setUTCHours(0, 0, 0, 0);
    return new Date(local.getTime() - offset * 60000);
}
export function startOfMonth(date = new Date(), timeZone = SHOP_TIME_ZONE) {
    const start = startOfDay(date, timeZone);
    const offset = zoneOffsetMinutes(start, timeZone);
    const local = new Date(start.getTime() + offset * 60000);
    local.setUTCDate(1);
    return new Date(local.getTime() - offset * 60000);
}
export function daysAgo(days, from = new Date()) {
    return new Date(from.getTime() - days * 86400000);
}
/** «5 хв тому», «вчора 14:05» — короткая человеческая метка для карточек. */
export function relativeTime(iso, now = new Date(), locale = 'uk-UA') {
    const then = new Date(iso);
    if (Number.isNaN(then.getTime()))
        return '—';
    const diffSec = Math.round((then.getTime() - now.getTime()) / 1000);
    const abs = Math.abs(diffSec);
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto', style: 'short' });
    if (abs < 60)
        return rtf.format(Math.round(diffSec), 'second');
    if (abs < 3600)
        return rtf.format(Math.round(diffSec / 60), 'minute');
    if (abs < 86400)
        return rtf.format(Math.round(diffSec / 3600), 'hour');
    if (abs < 86400 * 30)
        return rtf.format(Math.round(diffSec / 86400), 'day');
    return new Intl.DateTimeFormat(locale, {
        day: 'numeric',
        month: 'short',
        timeZone: SHOP_TIME_ZONE,
    }).format(then);
}
export function formatDateTime(iso, locale = 'uk-UA') {
    if (!iso)
        return '—';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime()))
        return '—';
    return new Intl.DateTimeFormat(locale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: SHOP_TIME_ZONE,
    }).format(date);
}
/**
 * Время без указания зоны («2026-10-09 15:00:00») → ISO в UTC.
 *
 * Так отдают время многие PHP-системы: в базе лежит местное время сервера,
 * а зона нигде не указана. Если не перевести, отчёты и границы суток
 * съезжают на величину смещения, причём незаметно.
 *
 * Строка с явной зоной (`Z` или `+03:00`) принимается как есть.
 */
export function naiveToUtcIso(value, timeZone = SHOP_TIME_ZONE) {
    const text = value?.trim();
    if (!text)
        return null;
    const hasZone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(text);
    if (hasZone) {
        const explicit = new Date(text);
        return Number.isNaN(explicit.getTime()) ? null : explicit.toISOString();
    }
    const parts = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/.exec(text);
    if (!parts) {
        const fallback = new Date(text);
        return Number.isNaN(fallback.getTime()) ? null : fallback.toISOString();
    }
    const [, year, month, day, hour, minute, second] = parts;
    // «0000-00-00 00:00:00» — нулевая дата MySQL, она же приезжает из многих
    // PHP-систем вместо пустого значения. Без этой проверки она превратилась бы
    // в год нулевой и уехала бы в начало любого списка.
    if (Number(year) < 1970 ||
        Number(month) < 1 ||
        Number(month) > 12 ||
        Number(day) < 1 ||
        Number(day) > 31) {
        return null;
    }
    const asIfUtc = Date.UTC(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second ?? 0));
    // Смещение зоны зависит от момента (лето/зима), а момент — от смещения.
    // Один уточняющий проход снимает расхождение на границе перехода.
    const firstOffset = zoneOffsetMinutes(new Date(asIfUtc), timeZone);
    let utc = asIfUtc - firstOffset * 60000;
    const secondOffset = zoneOffsetMinutes(new Date(utc), timeZone);
    if (secondOffset !== firstOffset)
        utc = asIfUtc - secondOffset * 60000;
    return new Date(utc).toISOString();
}
/** Границы месяца `ГГГГ-ММ` в зоне магазина, выраженные в UTC. */
export function monthRange(month, timeZone = SHOP_TIME_ZONE) {
    const [year, mon] = month.split('-').map(Number);
    if (!year || !mon || mon < 1 || mon > 12) {
        throw new Error(`Некорректный месяц: ${month}`);
    }
    // Берём середину месяца, чтобы не зависеть от смещения зоны и перехода на лето.
    const midMonth = new Date(Date.UTC(year, mon - 1, 15, 12, 0, 0));
    const from = startOfMonth(midMonth, timeZone);
    const midNext = new Date(Date.UTC(year, mon, 15, 12, 0, 0));
    const to = startOfMonth(midNext, timeZone);
    return { from, to };
}
//# sourceMappingURL=time.js.map